import React, { ChangeEvent, useEffect, useState, useRef } from 'react';

import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { Search } from '@mui/icons-material';
import classNames from 'classnames';

import { useDebounce } from '@vk-hr-tek/ui/hooks';
import { CircularProgress } from '@vk-hr-tek/ui/CircularProgress';
import { Box } from '@vk-hr-tek/ui/Box';
import { Typography } from '@vk-hr-tek/ui/Typography';
import { IconButton } from '@vk-hr-tek/ui/IconButton';
import { Icon } from '@vk-hr-tek/ui/Icon';
import { useInject } from '@vk-hr-tek/core/ioc';

import { UsersService } from '@app/gen/users';
import { ContentService } from '@app/gen/content';
import { useDispatch, useSelector } from '@app/hooks';

import { search, selectSearchStatus, selectSearchItems } from '../../../slice';

import { SearchOption } from './SearchOption';
import { ListBox } from './ListBox';
import {
  SearchInputProps,
  SearchCategory,
  SearchItem,
  LoadItemsFilter,
} from './SearchInput.types';
import { GroupFilter } from './GroupFilter';
import { useStyles } from './SearchInput.styles';

const DEBOUNCE_TIMEOUT = 200;
const DEFAULT_LIMIT_SINGLE_CATEGORY = 6;
const DEFAULT_WIDTH_SIZE = 224;
const NEWS_COVER_SIZE = {
  width: 120,
  height: 120,
};

const categories: SearchCategory[] = [
  {
    label: 'Новости',
    value: 'news',
    placeholder: 'Поиск по новостям',
  },
  {
    label: 'Комментарии',
    value: 'comments',
    placeholder: 'Поиск по комментариям',
  },
];

export const SearchInput = ({
  placeholder,
  disabled = false,
  limit = 50,
  onFocus,
  onBlur,
  minInputValueLength = 2,
  testId,
  onInputValueChange,
}: SearchInputProps) => {
  const [open, setOpen] = useState(false);

  const userService = useInject(UsersService);
  const contentApiService = useInject(ContentService);
  const dispatch = useDispatch();

  const width = open ? '100%' : DEFAULT_WIDTH_SIZE;

  const { classes } = useStyles({ width });

  const [currentCategory, setCurrentCategory] = useState<
    SearchCategory['value'] | 'all'
  >('all');

  const inputRef = useRef<HTMLDivElement | null>(null);
  const [inputWidth, setInputWidth] = useState(0);

  const currentOption = categories.find(
    (category) => category.value === currentCategory,
  );

  const status = useSelector(selectSearchStatus);
  const items = useSelector(selectSearchItems);

  const [inputValue, setInputValue] = useState('');
  const [selectedItem, setSelectedItem] = useState<SearchItem | null>(null);
  const [filter, setFilter] = useState<LoadItemsFilter>({
    limit,
    query: '',
    category: undefined,
  });

  const handleChangeCategory = (category: SearchCategory['value'] | 'all') =>
    setCurrentCategory(category);

  const loadPhoto = async ({
    type,
    id,
  }: {
    type: 'news' | 'comment';
    id: string;
  }) => {
    switch (type) {
      case 'news':
        const cover = await contentApiService.resizeImage({
          params: { attachment_id: id },
          query: NEWS_COVER_SIZE,
        });
        return cover.file;
      case 'comment':
        const avatar = await userService.photo({ params: { user_id: id } });
        return avatar.file;
    }
  };

  const debouncedInputValue = useDebounce(inputValue, DEBOUNCE_TIMEOUT);

  const requestItems = (query: string) => {
    dispatch(
      search({
        query,
        group: currentCategory === 'all' ? undefined : currentCategory,
        limits: {
          news: DEFAULT_LIMIT_SINGLE_CATEGORY,
          comments: DEFAULT_LIMIT_SINGLE_CATEGORY,
        },
      }),
    );
  };

  const autocompleteOpenHandler = () => setOpen(true);
  const autocompleteBlurHandler = () => {
    onBlur?.();
    setOpen(false);
  };
  const autocompleteFocusHandler = () => {
    onFocus?.();
    setInputValue('');
  };

  const autocompleteResetHandler = () => setInputValue('');
  const autocompleteGetInputValue = () => {
    if (!open && !inputValue) {
      return (selectedItem?.type || '').toString().split('\n').join(', ');
    }
    return inputValue;
  };

  const autocompleteInputChangeHandler = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    event: ChangeEvent<Record<string, any>>,
    nextValue: string,
    reason: string,
  ) => {
    if (reason === 'reset') {
      setInputValue('');
      setFilter({ ...filter, query: '' });
      return;
    }

    if (reason === 'clear') {
      setSelectedItem(null);
    }

    if (reason === 'input') {
      setFilter({ ...filter, query: nextValue });
    }

    if (onInputValueChange) {
      onInputValueChange(nextValue);
    }

    setInputValue(nextValue);
  };

  useEffect(() => {
    const handleTabSwitch = () => inputRef.current?.blur();

    document.addEventListener('visibilitychange', handleTabSwitch);
    return () => {
      document.removeEventListener('visibilitychange', handleTabSwitch);
    };
  }, []);

  useEffect(() => {
    if (!inputRef.current) return;
    const iconWidth = 38;

    const ro = new ResizeObserver(() => {
      const offsetWidth = inputRef.current?.offsetWidth;

      if (offsetWidth) {
        setInputWidth(offsetWidth + iconWidth);
      }
    });

    ro.observe(inputRef.current);

    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!open || debouncedInputValue.length < minInputValueLength) {
      return;
    }

    requestItems(debouncedInputValue);
    setFilter(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedInputValue, currentCategory]);

  useEffect(() => {
    if (!open) {
      autocompleteResetHandler();
    }
  }, [open]);

  const listCategories =
    categories.length > 0
      ? [
          {
            label: 'Везде',
            value: 'all',
          },
          ...categories,
        ]
      : [];

  const customPaperProps = {
    currentCategory,
    onChangeCategory: handleChangeCategory,
    categories: listCategories,
    showResult:
      debouncedInputValue.length >= minInputValueLength &&
      (status === 'complete' || status === 'loading'),
  };
  const customListboxProps = {
    loading: status === 'loading',
    sx: {
      padding: '12px 0',
      maxHeight: '500px',
    },
  };

  return (
    <Box className={classNames('aqa_autocomplete_input', classes.root)}>
      <Autocomplete
        blurOnSelect
        data-testid={testId}
        classes={{
          paper: classes.list,
          option: classes.option,
        }}
        open={open}
        filterOptions={(options) => options}
        getOptionLabel={(option) => option.type}
        getOptionDisabled={(option) => option.type === 'label'}
        onOpen={autocompleteOpenHandler}
        onBlur={autocompleteBlurHandler}
        onFocus={autocompleteFocusHandler}
        disabled={disabled}
        inputValue={autocompleteGetInputValue()}
        onInputChange={autocompleteInputChangeHandler}
        slotProps={{
          paper: {
            component: GroupFilter,
            sx: { width: inputWidth },
            ...customPaperProps,
          },
          popper: { sx: { width: inputWidth } },
          listbox: {
            component: ListBox,
            ...customListboxProps,
          },
        }}
        renderOption={(props, option) => {
          return (
            <li {...props} onClick={autocompleteResetHandler} key={option.id}>
              <SearchOption
                option={option as SearchItem}
                loadPhoto={loadPhoto}
              />
            </li>
          );
        }}
        options={
          status === 'loading' ||
          debouncedInputValue.length < minInputValueLength
            ? []
            : items
        }
        loading={status === 'loading'}
        loadingText={
          <Box display="flex" justifyContent="center" p="16">
            <CircularProgress size={30} />
          </Box>
        }
        noOptionsText={
          status === 'complete' &&
          inputValue.length >= minInputValueLength && (
            <Box
              height={56}
              py="24"
              pr="24"
              pl="16"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Typography variant="body2" color="text.light.secondary">
                Ничего не найдено
              </Typography>
            </Box>
          )
        }
        disableClearable
        popupIcon={null}
        renderInput={(params) => (
          <TextField
            {...params}
            inputRef={inputRef}
            classes={{
              root: `${classes.input} aqa_select`,
            }}
            variant="outlined"
            placeholder={currentOption?.placeholder || placeholder}
            slotProps={{
              input: {
                ...params.InputProps,
                startAdornment: (
                  <Search
                    className={classes.searchIcon}
                    fontSize="small"
                    color="inherit"
                  />
                ),
                endAdornment: (
                  <>
                    {!!inputValue && (
                      <Box px="8" position="absolute" right={0}>
                        <IconButton
                          onClick={autocompleteResetHandler}
                          blurOnClick={false}
                        >
                          <Icon name="dismiss_overlay" size="24" />
                        </IconButton>
                      </Box>
                    )}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
      />
    </Box>
  );
};
